import { useGetOverlappingCalculationsQuery } from '../../Configuration/apiTemplate';
import React, { useCallback, useEffect, useMemo } from 'react';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import { CalculationStatus, CalculationWithOverviewResponse } from '@ydistri/api-sdk';
import { Button, Column, Row, Text } from '@ydistri/ds';
import CalculationsTable from '../../Calculations/components/table/CalculationsTable';
import { isCalculationInTemporaryStatus } from '../../Calculations/calculationsLib';
import { resetSelectedRowKeys, setSelectedRowKeys } from '../../Calculations/calculationsSlice';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../store';
import { plural } from '@ydistri/generic-utils';
import { CalculationDataType } from '../../Calculations/calculationsTypes';

const emptyOverlaps: CalculationWithOverviewResponse[] = [];
export const OVERLAP_TABLE_ID = 'overlapTableId';

interface OverlappingCalculationFormProps {
  initialValues?: CalculationDataType[];
}

const OverlappingCalculationForm: React.FC<OverlappingCalculationFormProps> = ({
  initialValues,
}) => {
  const dispatch = useAppDispatch();

  const { data: overlappingCandidates = emptyOverlaps, isFetching } =
    useGetOverlappingCalculationsQuery();
  const selectedOverlappingCalculations = useSelector(
    (state: ReduxState) => state.calculations.selectedRowKeys[OVERLAP_TABLE_ID]?.arr,
  );

  const selectedOverlaps = useMemo(() => {
    if (!isFetching) {
      if (initialValues && initialValues.length > 0) {
        //if we have initial values, we want to filter them only to match those from overlap candidates - we do not care about older ones
        return initialValues.filter(iv => overlappingCandidates.some(bo => bo.id === iv.id));
      }
      // if we don't have any initial values, we just set to all of the overlapping candidates
      return overlappingCandidates;
    }
  }, [overlappingCandidates, initialValues, isFetching]);

  useEffect(() => {
    if (selectedOverlaps) {
      dispatch(
        setSelectedRowKeys({
          tableId: OVERLAP_TABLE_ID,
          data: selectedOverlaps.map(c => c.id),
        }),
      );
    }
  }, [selectedOverlaps, dispatch]);

  const clearSelectionHandler = useCallback(() => {
    dispatch(resetSelectedRowKeys(OVERLAP_TABLE_ID));
  }, [dispatch]);

  const selectableRules = useCallback(
    (c: CalculationWithOverviewResponse) =>
      c.status === CalculationStatus.Crashed || isCalculationInTemporaryStatus(c.status),
    [],
  );

  if (isFetching) {
    return <LoadingIndicator />;
  }

  const overlappingCalculationCount = (selectedOverlappingCalculations ?? []).length;

  return (
    <Column $gap="1rem">
      <Row $gap="2rem">
        <Text>
          {overlappingCalculationCount} overlapping calculation{plural(overlappingCalculationCount)}
        </Text>
        {overlappingCalculationCount > 0 && (
          <Button $sizeVariant="xSmall" onClick={clearSelectionHandler}>
            Clear selection
          </Button>
        )}
      </Row>
      {overlappingCandidates.length > 0 && (
        <CalculationsTable
          calculationsTableId={OVERLAP_TABLE_ID}
          variant="compact"
          showActions={false}
          displaySkuTypeValues={false}
          linksEnabled={false}
          selectableRules={selectableRules}
          predefinedCalculations={overlappingCandidates}
          autoHeight={true}
        />
      )}
    </Column>
  );
};

export default OverlappingCalculationForm;
